import { useCallback, useEffect, useState } from "react";

import type { Point } from "../../editor/types";

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;

const PAD = 40;
const PAD_NARROW = 16;
const NARROW_WIDTH = 760;
/** Screen pixels of the image that must stay visible while panning. */
const KEEP_VISIBLE = 80;

export function clampZoom(value: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
}

interface ViewportOptions {
  size: { width: number; height: number };
  imageWidth: number;
  imageHeight: number;
  zoom: number;
  topInset: number;
  /** Changing this re-centres the image (new image, "fit" pressed). */
  resetKey: string;
  onZoomChange: (zoom: number) => void;
}

/**
 * Fit-to-screen scale times the user's zoom, plus a pan offset. Zoom 1 always
 * means "whole image visible", so the bottom bar's 100% is the fitted view.
 */
export function useViewport({
  size,
  imageWidth,
  imageHeight,
  zoom,
  topInset,
  resetKey,
  onZoomChange,
}: ViewportOptions) {
  const [pan, setPanState] = useState<Point>({ x: 0, y: 0 });
  const [panKey, setPanKey] = useState(resetKey);
  if (panKey !== resetKey) {
    setPanKey(resetKey);
    setPanState({ x: 0, y: 0 });
  }

  const pad = size.width < NARROW_WIDTH ? PAD_NARROW : PAD;
  const fitScale = Math.max(
    0.001,
    Math.min(
      (size.width - pad * 2) / imageWidth,
      (size.height - pad * 2 - topInset) / imageHeight,
    ),
  );
  const scale = fitScale * zoom;

  const origin = useCallback(
    (nextScale: number) => ({
      x: (size.width - imageWidth * nextScale) / 2,
      y: (size.height + topInset - imageHeight * nextScale) / 2,
    }),
    [imageHeight, imageWidth, size.height, size.width, topInset],
  );

  const clampPan = useCallback(
    (next: Point, nextScale: number): Point => {
      const base = origin(nextScale);
      const width = imageWidth * nextScale;
      const height = imageHeight * nextScale;
      const keepX = Math.min(KEEP_VISIBLE, width);
      const keepY = Math.min(KEEP_VISIBLE, height);
      return {
        x: Math.max(keepX - width - base.x, Math.min(size.width - keepX - base.x, next.x)),
        y: Math.max(keepY - height - base.y, Math.min(size.height - keepY - base.y, next.y)),
      };
    },
    [imageHeight, imageWidth, origin, size.height, size.width],
  );

  const setPan = useCallback(
    (next: Point | ((current: Point) => Point)) => {
      setPanState((current) =>
        clampPan(typeof next === "function" ? next(current) : next, scale),
      );
    },
    [clampPan, scale],
  );

  // Keep the image reachable when the window shrinks or zoom changes.
  useEffect(() => {
    setPanState((current) => {
      const clamped = clampPan(current, scale);
      return clamped.x === current.x && clamped.y === current.y ? current : clamped;
    });
  }, [clampPan, scale]);

  const base = origin(scale);
  const view = { x: base.x + pan.x, y: base.y + pan.y, scale };

  const toImage = useCallback(
    (screen: Point): Point => ({
      x: (screen.x - view.x) / view.scale,
      y: (screen.y - view.y) / view.scale,
    }),
    [view.scale, view.x, view.y],
  );

  /** Zooms about a fixed screen point, keeping the image under it in place. */
  const zoomAround = useCallback(
    (screen: Point, nextZoom: number, from = { pan, zoom }) => {
      const clamped = clampZoom(nextZoom);
      const fromScale = fitScale * from.zoom;
      const fromOrigin = origin(fromScale);
      const imageX = (screen.x - fromOrigin.x - from.pan.x) / fromScale;
      const imageY = (screen.y - fromOrigin.y - from.pan.y) / fromScale;
      const nextScale = fitScale * clamped;
      const nextOrigin = origin(nextScale);
      setPanState(
        clampPan(
          {
            x: screen.x - imageX * nextScale - nextOrigin.x,
            y: screen.y - imageY * nextScale - nextOrigin.y,
          },
          nextScale,
        ),
      );
      onZoomChange(clamped);
    },
    [clampPan, fitScale, onZoomChange, origin, pan, zoom],
  );

  return { view, pan, setPan, toImage, zoomAround };
}
